"use client";

import Image from "next/image";
import { ColumnDef } from "@/components/shared/data-table";
import { GameImage, GamePrice } from "@/components/shared";
import { ExternalLink } from "lucide-react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type {
  PublisherListItem,
  PublisherGame,
  PublisherTopSellerGame,
  PublisherEventFeedItem,
} from "../types";
import { RANK_LIST_CONFIGS } from "@/features/rank-list/shared";
import type { SkeletonColumn } from "@/components/shared/list-skeleton/types";
import { parsePriceFormatted } from "../utils/parsePrice";
import { getReviewScoreColor, shortenReviewLabel } from "../utils/getReviewScoreColor";
import { formatDateShort, formatRelativeTime } from "@/lib/utils/date";
import {
  formatPublisherEventText,
  getPublisherEventLabel,
  getPublisherEventTone,
} from "../utils/formatPublisherEventText";

const PRIMARY_COLOR = RANK_LIST_CONFIGS.followers.primaryColor;
const TOPSELLERS_COLOR = RANK_LIST_CONFIGS.topsellers.primaryColor;

function GameCell({
  appid,
  gameName,
}: {
  appid: number;
  gameName: string | null;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleFilterByGame = (e: React.MouseEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("game_filter", appid.toString());
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleOpenInNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(`/app/${appid}`, "_blank");
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleFilterByGame}
        className="text-[#66c0f4] hover:text-white transition-colors text-left"
      >
        {gameName ?? `App ${appid}`}
      </button>
      <button
        onClick={handleOpenInNewTab}
        className="text-[#8f98a0] hover:text-[#66c0f4] transition-colors flex-shrink-0"
        title="Open in new tab"
      >
        <ExternalLink className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export const publisherColumns: ColumnDef<PublisherListItem>[] = [
  {
    header: "Rank",
    accessorKey: "rank",
    cell: (item) => (
      <span className="font-bold text-[13px]" style={{ color: PRIMARY_COLOR }}>
        {item.rank != null ? `#${item.rank}` : "—"}
      </span>
    ),
    width: "w-16",
  },
  {
    header: "Avatar",
    noPadding: true,
    cell: (item) => (
      <div className="w-[37px] h-[37px] flex-shrink-0 relative">
        {item.avatar ? (
          <Image
            src={item.avatar}
            alt={item.name}
            fill
            className="object-cover"
            sizes="37px"
          />
        ) : (
          <div className="w-full h-full bg-[#2a475e] flex items-center justify-center text-[#8f98a0] text-[10px]">
            No
          </div>
        )}
      </div>
    ),
  },
  {
    header: "Name",
    accessorKey: "name",
    sortable: true,
    sortKey: "name",
    cell: (item) => (
      <span className="text-white font-medium text-[13px]">{item.name}</span>
    ),
  },
  {
    header: "Followers",
    accessorKey: "followers",
    sortable: true,
    sortKey: "followers",
    align: "right",
    width: "w-28",
    cell: (item) => (
      <span className="font-bold text-[13px]" style={{ color: PRIMARY_COLOR }}>
        {item.followers.toLocaleString("en-US")}
      </span>
    ),
  },
  {
    header: "Total Players",
    align: "right",
    width: "w-28",
    cell: (item) =>
      item.total_players > 0 ? (
        <span
          className="font-semibold text-[13px] tabular-nums"
          style={{ color: "#a4d007" }}
          title="Avg concurrent players across all their games (latest day)"
        >
          {item.total_players.toLocaleString("en-US")}
        </span>
      ) : (
        <span className="text-[#8f98a0] text-[13px]">—</span>
      ),
  },
  {
    header: "Apps",
    align: "right",
    width: "w-20",
    cell: (item) => (
      <span className="text-[#c7d5e0] text-[13px] tabular-nums">
        {item.apps_count > 0 ? item.apps_count.toLocaleString("en-US") : "—"}
      </span>
    ),
  },
  {
    header: "In Top Sellers",
    align: "right",
    width: "w-28",
    cell: (item) =>
      item.topsellers_apps > 0 ? (
        <span
          className="font-semibold text-[13px] tabular-nums"
          style={{ color: TOPSELLERS_COLOR }}
          title={`${item.topsellers_apps} app(s) currently in Steam top sellers`}
        >
          {item.topsellers_apps.toLocaleString("en-US")}
        </span>
      ) : (
        <span className="text-[#8f98a0] text-[13px]">—</span>
      ),
  },
];

export const publisherGamesColumns: ColumnDef<PublisherGame>[] = [
  {
    header: "Image",
    noPadding: true,
    width: "w-[100px]",
    cell: (game) => (
      <div className="w-[100px] h-[37px] flex-shrink-0">
        <GameImage
          src={game.capsule_image || game.header_image}
          alt={game.name}
          width={100}
          height={37}
        fillContainer
        />
      </div>
    ),
  },
  {
    header: "Name",
    accessorKey: "name",
    cell: (game) => (
      <span className="text-white font-medium text-[13px]">{game.name}</span>
    ),
  },
  {
    header: "Release",
    accessorKey: "release_date",
    sortable: true,
    sortKey: "release_ts_utc",
    cell: (game) => (
      <span className="text-[#8f98a0] text-[11px]">
        {game.coming_soon ? "Coming Soon" : game.release_date || "—"}
      </span>
    ),
  },
  {
    header: "Price",
    cell: (game) => {
      const { isFree, priceFinal } = parsePriceFormatted(
        game.price_final_formatted
      );
      return <GamePrice isFree={isFree} priceFinal={priceFinal} />;
    },
  },
  {
    header: "Reviews",
    accessorKey: "total_reviews",
    sortable: true,
    sortKey: "total_reviews",
    cell: (game) => {
      if (!game.total_reviews || game.total_reviews === 0) {
        return <span className="text-[#8f98a0] text-[11px]">—</span>;
      }

      return (
        <div className="flex flex-col gap-0.5">
          {game.review_score_label && (
            <span
              className={`text-[11px] font-semibold ${getReviewScoreColor(
                game.percent_positive
              )}`}
            >
              {shortenReviewLabel(game.review_score_label)}
            </span>
          )}
          <span className="text-[#c7d5e0] text-[11px]">
            {game.total_reviews.toLocaleString("en-US")}
            {game.percent_positive !== null && (
              <span className="text-[#8f98a0] ml-1">
                ({game.percent_positive}%)
              </span>
            )}
          </span>
        </div>
      );
    },
  },
  {
    header: "Followers",
    accessorKey: "followers",
    sortable: true,
    sortKey: "followers",
    align: "right",
    cell: (game) => (
      <span className="font-bold text-[13px]" style={{ color: PRIMARY_COLOR }}>
        {game.followers != null ? game.followers.toLocaleString("en-US") : "—"}
      </span>
    ),
  },
];

export const publisherTopSellersColumns: ColumnDef<PublisherTopSellerGame>[] = [
  {
    header: "Rank",
    accessorKey: "rank",
    cell: (game) => (
      <span className="font-bold text-[13px]" style={{ color: PRIMARY_COLOR }}>
        #{game.rank}
      </span>
    ),
    width: "w-16",
  },
  {
    header: "Image",
    noPadding: true,
    width: "w-[100px]",
    cell: (game) => (
      <div className="w-[100px] h-[37px] flex-shrink-0">
        <GameImage
          src={game.capsule_image || game.header_image}
          alt={game.name}
          width={100}
          height={37}
        fillContainer
        />
      </div>
    ),
  },
  {
    header: "Name",
    accessorKey: "name",
    cell: (game) => (
      <span className="text-white font-medium text-[13px]">{game.name}</span>
    ),
  },
  {
    header: "Release",
    accessorKey: "release_date",
    cell: (game) => (
      <span className="text-[#8f98a0] text-[11px]">
        {game.coming_soon ? "Coming Soon" : game.release_date || "—"}
      </span>
    ),
  },
  {
    header: "Price",
    cell: (game) => {
      const { isFree, priceFinal } = parsePriceFormatted(
        game.price_final_formatted
      );
      return <GamePrice isFree={isFree} priceFinal={priceFinal} />;
    },
  },
  {
    header: "Reviews",
    accessorKey: "total_reviews",
    cell: (game) => {
      if (!game.total_reviews || game.total_reviews === 0) {
        return <span className="text-[#8f98a0] text-[11px]">—</span>;
      }

      return (
        <div className="flex flex-col gap-0.5">
          {game.review_score_label && (
            <span
              className={`text-[11px] font-semibold ${getReviewScoreColor(
                game.percent_positive
              )}`}
            >
              {shortenReviewLabel(game.review_score_label)}
            </span>
          )}
          <span className="text-[#c7d5e0] text-[11px]">
            {game.total_reviews.toLocaleString("en-US")}
            {game.percent_positive !== null && (
              <span className="text-[#8f98a0] ml-1">
                ({game.percent_positive}%)
              </span>
            )}
          </span>
        </div>
      );
    },
  },
];

export const publisherGamesSkeletonConfig: SkeletonColumn[] = [
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Reviews", type: "number", width: "w-20" },
  { label: "Followers", type: "number", width: "w-20" },
];

export const publisherTopSellersSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-16" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Reviews", type: "number", width: "w-20" },
];

export const publisherEventsColumns: ColumnDef<PublisherEventFeedItem>[] = [
  {
    header: "Date",
    cell: (e) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-[#c7d5e0] text-xs">
          {e.event_day || formatDateShort(e.ts)}
        </span>
        <span className="text-[#8f98a0] text-[10px]">
          {formatRelativeTime(e.ts)}
        </span>
      </div>
    ),
    width: "w-36",
  },
  {
    header: "Event",
    cell: (e) => (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] px-2 py-0.5 rounded border bg-[#1b2838] ${
              getPublisherEventTone(e.event_type) === "wishlist"
                ? "text-[#b18cff] border-[#5a3d7a]"
                : getPublisherEventTone(e.event_type) === "topsellers"
                ? "text-[#ffcc6f] border-[#6b5530]"
                : getPublisherEventTone(e.event_type) === "ccu"
                ? "text-[#a4d007] border-[#3b4a1a]"
                : "text-[#c7d5e0] border-[#2a475e]"
            }`}
          >
            {getPublisherEventLabel(e.event_type)}
          </span>
        </div>
        <span className="text-white text-sm">
          {(() => {
            const text = formatPublisherEventText(e);
            const tone = getPublisherEventTone(e.event_type);
            if (tone === "wishlist" || tone === "topsellers") {
              // highlight rank-like tokens (#123)
              return text.split(/(#[0-9]+)/g).map((part, idx) =>
                /^#[0-9]+$/.test(part) ? (
                  <span key={idx} className="text-[#66c0f4] font-semibold">
                    {part}
                  </span>
                ) : (
                  <span key={idx}>{part}</span>
                )
              );
            }
            if (tone === "ccu") {
              // highlight numeric values
              return text.split(/([0-9]+)/g).map((part, idx) =>
                /^[0-9]+$/.test(part) ? (
                  <span key={idx} className="text-[#a4d007] font-semibold">
                    {part}
                  </span>
                ) : (
                  <span key={idx}>{part}</span>
                )
              );
            }
            return text;
          })()}
        </span>
      </div>
    ),
  },
  {
    header: "Game",
    cell: (e) =>
      e.appid != null ? (
        <GameCell appid={e.appid} gameName={e.game_name} />
      ) : (
        <span className="text-[#8f98a0]">—</span>
      ),
  },
];

export const publisherEventsSkeletonConfig: SkeletonColumn[] = [
  { label: "Date", type: "date", width: "w-24" },
  { label: "Event", type: "text", width: "w-80" },
  { label: "Game", type: "text", width: "w-48" },
];
